<template>
    <div class="picking-modal" role="presentation" @mousedown.self="$emit('close')">
        <section class="picking-dialog picking-dialog--form" role="dialog" aria-modal="true" aria-labelledby="picking-title">
            <header class="picking-dialog__header">
                <div class="picking-dialog__header-content">
                    <h2 id="picking-title">Picklijst {{ picking.code }}</h2>
                    <div class="picking-dialog__header-meta" :class="{'picking-dialog__header-meta--without-order-info': !hasOrderInfo}">
                        <div class="picking-dialog__meta-group">
                            <span class="picking-dialog__meta-title">Bestelling</span>
                            <p>
                                <i class="ak-icon">receipt_long</i>
                                Ordernummer: <a href="#" @click.prevent="$emit('open-order')">{{ picking.orderCode }}</a>
                            </p>
                            <p v-if="picking.externalOrderId"><i class="ak-icon">confirmation_number</i> Extern ordernummer: {{ picking.externalOrderId }}</p>
                        </div>
                        <div class="picking-dialog__meta-group">
                            <span class="picking-dialog__meta-title">Klant</span>
                            <p><i class="ak-icon">person_outline</i> {{ picking.customerName }}</p>
                            <p>
                                <i class="ak-icon">near_me</i> {{ picking.address }}
                                <i v-if="picking.hasSuspiciousShippingAddress"
                                   class="ak-icon picking-dialog__address-warning"
                                   title="Controleer het leveringsadres: straatnaam en huisnummer lijken verwisseld of dubbel ingevuld."
                                   aria-label="Controleer het leveringsadres: straatnaam en huisnummer lijken verwisseld of dubbel ingevuld.">warning</i>
                            </p>
                        </div>
                        <section v-if="hasOrderInfo" class="picking-order-info picking-dialog__meta-group" aria-label="Orderinformatie">
                            <div v-if="picking.requestedDeliveryDate" class="picking-order-info__item">
                                <span>Gewenste leverdatum</span>
                                <strong>{{ picking.requestedDeliveryDate }}</strong>
                            </div>
                            <div v-if="picking.externalComments" class="picking-order-info__item picking-order-info__comments">
                                <span>Opmerkingen van de klant</span>
                                <p>{{ picking.externalComments }}</p>
                            </div>
                        </section>
                    </div>
                </div>
                <button type="button" class="picking-dialog__close" aria-label="Sluiten" @click="$emit('close')">
                    <i class="ak-icon">close</i>
                </button>
            </header>

            <form id="picking-v2-form" class="picking-dialog__body" @submit.prevent="save">
                <div class="picking-dialog__intro">
                    <div class="picking-field">
                        <label for="picker-name">Naam orderpicker *</label>
                        <input id="picker-name" ref="pickerName" v-model="form.pickerName" :class="{'is-invalid': fields.pickerName}">
                        <small v-if="fields.pickerName">{{ fields.pickerName }}</small>
                    </div>
                    <div class="picking-field">
                        <label for="picking-remarks">Interne opmerkingen picking</label>
                        <textarea id="picking-remarks" v-model="form.remarks" rows="1" placeholder="Typ hier…"></textarea>
                    </div>
                </div>

                <div class="picking-dialog__columns" :class="{'picking-dialog__columns--without-order-lines': !form.items.length}">
                    <section>
                        <h3 v-if="form.items.length">Bestelling</h3>
                        <div v-if="form.items.length" class="picking-lines">
                            <article v-for="item in form.items" :key="item.id" class="picking-line">
                                <div class="picking-line__name">
                                    <span>{{ item.title }}</span>
                                    <small>#{{ item.code }}</small>
                                </div>
                                <counter-control
                                    :value="item.picked"
                                    :maximum="item.maximumPickable"
                                    :display="`${item.picked}/${item.requested}`"
                                    @input="item.picked = $event"
                                />
                                <small v-if="fields['items.' + item.id]" class="picking-line__error">
                                    {{ fields['items.' + item.id] }}
                                </small>
                            </article>
                        </div>
                        <section class="picking-shipping" :class="{'picking-shipping--without-order-lines': !form.items.length}">
                            <h3>Verzending</h3>
                            <div class="picking-shipping__option" :class="{'picking-shipping__option--disabled': isOtherDelivery}" :aria-disabled="isOtherDelivery">
                                <div class="picking-shipping__row">
                                    <span id="bpost-labels-label">Aantal Bpost-labels</span>
                                    <counter-control
                                        aria-labelledby="bpost-labels-label"
                                        :value="form.shipping.numberOfBoxes"
                                        :display="form.shipping.numberOfBoxes || '–'"
                                        :disabled="isOtherDelivery"
                                        @input="setNumberOfBoxes($event)"
                                    />
                                </div>
                                <small v-if="fields['shipping.numberOfBoxes']" class="picking-shipping__error">{{ fields['shipping.numberOfBoxes'] }}</small>
                                <div v-if="form.shipping.numberOfBoxes > 0" class="picking-shipping__type">
                                    <label for="box-type">Bpost-pakkettype *</label>
                                    <select id="box-type" v-model="form.shipping.boxType" required>
                                        <option disabled value="">Kies een Bpost-pakkettype</option>
                                        <option v-for="type in form.shipping.boxTypes" :key="type" :value="type">{{ type }}</option>
                                    </select>
                                </div>
                                <small v-if="fields['shipping.boxType']" class="picking-shipping__error">{{ fields['shipping.boxType'] }}</small>
                                <div v-if="form.shipping.numberOfBoxes > 0" class="picking-shipping__weight">
                                    <label for="shipment-weight">Totaal gewicht verzending</label>
                                    <div class="picking-shipping__weight-input">
                                        <input id="shipment-weight" ref="shipmentWeight" type="text" inputmode="decimal" pattern="[0-9]+(,[0-9]{1,3})?" title="Gebruik alleen cijfers en een komma, met maximaal drie decimalen." :value="weightInputDirty ? weightInputValue : totalWeight" :class="{'is-invalid': weightError || fields['shipping.shipmentWeightInGrams']}" @input="validateShipmentWeightInput($event.target)" @change="setShipmentWeight($event.target)" @focus="$event.target.select()">
                                        <span>kg</span>
                                    </div>
                                    <small>Gebruik een komma voor decimalen, bijvoorbeeld 02,500 kg.</small>
                                    <small v-if="weightError || fields['shipping.shipmentWeightInGrams']" class="picking-shipping__error">{{ weightError || fields['shipping.shipmentWeightInGrams'] }}</small>
                                </div>
                            </div>
                            <div class="picking-shipping__option" :class="{'picking-shipping__option--disabled': isOtherDelivery}" :aria-disabled="isOtherDelivery">
                                <div class="picking-shipping__row">
                                    <span id="envelopes-label">Aantal enveloppen te frankeren</span>
                                    <counter-control
                                        aria-labelledby="envelopes-label"
                                        :value="form.shipping.numberOfEnvelopes"
                                        :display="form.shipping.numberOfEnvelopes || '–'"
                                        :disabled="isOtherDelivery"
                                        @input="setNumberOfEnvelopes($event)"
                                    />
                                </div>
                                <small v-if="fields['shipping.numberOfEnvelopes']" class="picking-shipping__error">{{ fields['shipping.numberOfEnvelopes'] }}</small>
                            </div>
                            <label class="picking-shipping__option picking-shipping__row picking-shipping__row--other">
                                <span>Andere levering (bv. pallet of afhalen)</span>
                                <input class="picking-shipping__switch" type="checkbox" :checked="isOtherDelivery" @change="setOtherDelivery($event.target.checked)">
                            </label>
                        </section>
                    </section>
                    <section>
                        <h3>Kies verpakking</h3>
                        <div class="picking-lines">
                            <article
                                v-for="material in orderedMaterials"
                                :key="material.id"
                                class="picking-line picking-line--material"
                                :class="{'is-addable': material.amount === 0}"
                                @click="addMaterial(material)"
                            >
                                <div class="picking-line__name"><span>{{ material.title }}</span></div>
                                <counter-control
                                    :value="material.amount"
                                    :display="material.amount || '–'"
                                    editable
                                    @input="material.amount = $event"
                                    @click.native.stop
                                />
                                <small v-if="fields['materials.' + material.id]" class="picking-line__error">
                                    {{ fields['materials.' + material.id] }}
                                </small>
                            </article>
                        </div>
                    </section>
                </div>

                <p v-if="fields.picking" class="picking-dialog__error" role="alert">{{ fields.picking }}</p>
                <p v-else-if="error" class="picking-dialog__error" role="alert">{{ error }}</p>
            </form>

            <footer class="picking-dialog__footer">
                <button type="button" class="picking-button picking-button--text" :disabled="saving" @click="$emit('close')">Annuleren</button>
                <button type="submit" form="picking-v2-form" class="picking-button picking-button--primary" :disabled="saving || (!hasActualPickingChange && !weightInputDirty)">
                    {{ saving ? 'Opslaan…' : 'Opslaan' }}
                </button>
            </footer>
        </section>
    </div>
</template>

<script>
const CounterControl = {
    props: {
        value: {type: Number, required: true},
        maximum: {type: Number, default: null},
        display: {type: [String, Number], required: true},
        editable: {type: Boolean, default: false},
        disabled: {type: Boolean, default: false},
    },
    methods: {
        change(delta) {
            if (this.disabled) return;
            const next = Math.max(0, this.value + delta);
            this.$emit('input', this.maximum === null ? next : Math.min(next, this.maximum));
        },
        input(event) {
            if (this.disabled) return;
            const next = Number(event.target.value);
            if (event.target.value === '' || !Number.isInteger(next) || next < 0) return;

            const normalized = this.maximum === null ? next : Math.min(next, this.maximum);
            this.$emit('input', normalized);
            this.$nextTick(() => { event.target.value = this.value; });
        },
        resetInput(event) {
            event.target.value = this.value;
        },
    },
    template: `
        <div class="picking-counter">
            <button type="button" aria-label="Verminder aantal" :disabled="disabled || value <= 0" @click="change(-1)">
                <i class="ak-icon">remove</i>
            </button>
            <input
                v-if="editable"
                type="number"
                :disabled="disabled"
                min="0"
                :max="maximum"
                step="1"
                inputmode="numeric"
                aria-label="Aantal"
                :value="value"
                @input="input"
                @blur="resetInput"
                @focus="$event.target.select()"
                @keydown.enter.prevent="$event.target.blur()"
            >
            <strong v-else aria-live="polite">{{ display }}</strong>
            <button type="button" class="is-primary" aria-label="Verhoog aantal" :disabled="disabled || (maximum !== null && value >= maximum)" @click="change(1)">
                <i class="ak-icon">add</i>
            </button>
        </div>`,
};

const PICKER_NAME_STORAGE_KEY = 'ijsedal.picking-v2.picker-name';

export default {
    name: 'PickingV2Form',
    components: {CounterControl},
    props: {
        picking: {type: Object, required: true},
        saving: {type: Boolean, default: false},
        fields: {type: Object, default: () => ({})},
        error: {type: String, default: ''},
    },
    data() {
        return {form: JSON.parse(JSON.stringify(this.picking)), initialPickingValues: null, weightError: '', weightInputDirty: false, weightInputValue: ''};
    },
    computed: {
        hasOrderInfo() {
            return Boolean(this.picking.requestedDeliveryDate || this.picking.externalComments);
        },
        orderedMaterials() {
            return [...this.form.materials].sort((left, right) => left.orderIndex - right.orderIndex);
        },
        hasActualPickingChange() {
            return this.initialPickingValues !== null
                && JSON.stringify(this.getPickingValues()) !== JSON.stringify(this.initialPickingValues);
        },
        isOtherDelivery() {
            return this.form.shipping.boxType === this.form.shipping.otherDelivery;
        },
        totalWeight() {
            const manual = this.form.shipping.shipmentWeightInGrams;
            const grams = manual === null || manual === ''
                ? this.form.items.reduce((total, item) => total + item.picked * item.weightInGrams, 0)
                : Number(manual);
            return (grams / 1000).toFixed(3).replace('.', ',').padStart(6, '0');
        },
    },
    mounted() {
        const savedPickerName = window.localStorage.getItem(PICKER_NAME_STORAGE_KEY);
        if (savedPickerName) {
            this.form.pickerName = savedPickerName;
        }
        this.initialPickingValues = this.getPickingValues();
        this.$refs.pickerName.focus();
    },
    watch: {
        'form.pickerName'(pickerName) {
            const normalizedPickerName = pickerName.trim();
            if (normalizedPickerName) {
                window.localStorage.setItem(PICKER_NAME_STORAGE_KEY, normalizedPickerName);
            }
        },
    },
    methods: {
        validateShipmentWeightInput(input) {
            this.weightInputValue = input.value;
            this.weightInputDirty = true;
            this.weightError = /[^0-9,]/.test(input.value) || !/^[0-9]*(,[0-9]{0,3})?$/.test(input.value)
                ? 'Gebruik alleen cijfers en één komma, met maximaal drie decimalen.'
                : '';
        },
        setShipmentWeight(input) {
            const value = input.value;
            if (value === '') {
                this.form.shipping.shipmentWeightInGrams = null;
                this.weightError = '';
                this.weightInputDirty = false;
                input.value = this.totalWeight;
                return true;
            }
            const grams = Math.round(Number(value.replace(',', '.')) * 1000);
            if (/[^0-9,]/.test(value) || !/^[0-9]+(,[0-9]{1,3})?$/.test(value) || grams <= 0 || grams > 2147483647) {
                this.weightError = 'Vul een positief gewicht in met alleen cijfers en een komma (bv. 02,500).';
                return false;
            }
            this.form.shipping.shipmentWeightInGrams = grams;
            this.weightError = '';
            this.weightInputDirty = false;
            input.value = this.totalWeight;
            return true;
        },
        setNumberOfBoxes(count) {
            this.form.shipping.numberOfBoxes = count;
            if (count > 0 && (!this.form.shipping.boxType || this.isOtherDelivery)) {
                this.form.shipping.boxType = this.form.shipping.defaultBoxType;
            }
            if (count === 0 && !this.isOtherDelivery) {
                this.form.shipping.boxType = '';
            }
        },
        setOtherDelivery(checked) {
            this.form.shipping.boxType = checked ? this.form.shipping.otherDelivery : '';
            if (checked) {
                this.form.shipping.numberOfBoxes = 0;
                this.form.shipping.numberOfEnvelopes = 0;
            }
        },
        setNumberOfEnvelopes(count) {
            this.form.shipping.numberOfEnvelopes = count;
            if (count > 0 && this.isOtherDelivery) {
                this.form.shipping.boxType = '';
            }
        },
        getPickingValues() {
            return {
                items: this.form.items.map(item => item.picked),
                materials: this.form.materials.map(material => material.amount),
                numberOfBoxes: this.form.shipping.numberOfBoxes,
                shipmentWeightInGrams: this.form.shipping.shipmentWeightInGrams === '' ? null : this.form.shipping.shipmentWeightInGrams,
                boxType: this.form.shipping.boxType,
                numberOfEnvelopes: this.form.shipping.numberOfEnvelopes,
                remarks: this.form.remarks,
            };
        },
        addMaterial(material) {
            if (material.amount === 0) {
                material.amount = 1;
            }
        },
        save() {
            if (this.form.shipping.numberOfBoxes > 0 && this.weightInputDirty && !this.setShipmentWeight(this.$refs.shipmentWeight)) {
                return;
            }
            this.$emit('save', {
                pickerName: this.form.pickerName,
                remarks: this.form.remarks,
                items: this.form.items.map(({id, picked}) => ({id, picked})),
                materials: this.form.materials.map(({id, amount}) => ({id, amount})),
                shipping: {
                    numberOfBoxes: this.form.shipping.numberOfBoxes,
                    shipmentWeightInGrams: this.form.shipping.shipmentWeightInGrams === '' ? null : this.form.shipping.shipmentWeightInGrams,
                    boxType: this.form.shipping.boxType,
                    numberOfEnvelopes: this.form.shipping.numberOfEnvelopes,
                },
            });
        },
    },
};
</script>

<style lang="scss">
.picking-modal {position: fixed; inset: 0; z-index: 1100; display: flex; align-items: center; justify-content: center; padding: 16px; background: rgba(17, 24, 39, .25); backdrop-filter: blur(3px);}
.picking-dialog {--picking-dialog-surface: #f9fafb; --picking-dialog-panel: #fff; --picking-dialog-text: #111827; --picking-dialog-muted: #6b7280; --picking-dialog-border: #e5e7eb; --picking-dialog-control: #f3f4f6; --picking-dialog-control-muted: rgba(243, 244, 246, .35); display: flex; flex-direction: column; width: min(1500px, 100%); max-height: calc(100vh - 32px); max-height: calc(100dvh - 32px); color: var(--picking-dialog-text); background: var(--picking-dialog-surface); border: 1px solid var(--picking-dialog-border); border-radius: 10px; box-shadow: 0 10px 24px rgba(0, 0, 0, .14); overflow: hidden;}
.picking-dialog--form {height: clamp(760px, 92vh, 1200px); height: clamp(760px, 92dvh, 1200px);}
.ak-dark .picking-dialog {--picking-dialog-surface: #12181f; --picking-dialog-panel: #18232d; --picking-dialog-text: #f9fafb; --picking-dialog-muted: #9ca3af; --picking-dialog-border: rgba(255, 255, 255, .2); --picking-dialog-control: rgba(255, 255, 255, .1); --picking-dialog-control-muted: rgba(255, 255, 255, .035);}
.picking-dialog__header {display: flex; justify-content: space-between; padding: 36px 30px 12px;}
.picking-dialog--form .picking-dialog__header {position: relative; flex: 0 0 auto; padding: 28px 30px 24px; background: var(--picking-dialog-panel); border-bottom: 1px solid var(--picking-dialog-border);}
.picking-dialog__header-content {width: 100%; min-width: 0;}
.picking-dialog__header-meta {display: grid; width: 100%; grid-template-columns: minmax(220px, 1fr) minmax(280px, 1fr) minmax(360px, 2fr);}
.picking-dialog__header-meta--without-order-info {grid-template-columns: repeat(2, minmax(280px, 1fr));}
.picking-dialog__meta-group {min-width: 0; padding: 4px 24px; border-right: 1px solid var(--picking-dialog-border);}
.picking-dialog__meta-group:first-child {padding-left: 0;}
.picking-dialog__meta-group:last-child {border-right: 0;}
.picking-dialog__meta-title {display: block; margin-bottom: 8px; color: var(--picking-dialog-muted); font-size: 14px;}
.picking-v2 .picking-dialog__header h2 {margin: 0 0 16px; padding-right: 58px; color: var(--picking-dialog-text); font-family: Poppins, sans-serif; font-size: 24px; font-style: normal; font-weight: 600; letter-spacing: 0; line-height: normal;}
.picking-dialog__header p {display: flex; align-items: center; gap: 8px; margin: 4px 0; font-size: 16px;}
.picking-dialog__header p .ak-icon {color: #298afb; font-size: 20px;}
.picking-dialog__header p .picking-dialog__address-warning {color: var(--color-warning);}
.picking-dialog__close {display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; margin: -24px -18px 0 0; color: var(--picking-dialog-text); background: var(--picking-dialog-panel); border: 0; border-radius: 50%; cursor: pointer;}
.picking-dialog--form .picking-dialog__close {position: absolute; top: 16px; right: 16px; margin: 0;}
.picking-dialog__close .ak-icon {font-size: 30px;}
.picking-dialog__body {min-height: 0; flex: 1 1 auto; padding: 24px 30px; overflow-y: auto; scrollbar-color: #cbd5e1 transparent; scrollbar-width: thin;}
.picking-dialog__body::-webkit-scrollbar {width: 8px;}
.picking-dialog__body::-webkit-scrollbar-track {background: transparent;}
.picking-dialog__body::-webkit-scrollbar-thumb {background: #cbd5e1; border: 2px solid transparent; border-radius: 999px; background-clip: padding-box;}
.picking-dialog__body::-webkit-scrollbar-thumb:hover {background-color: #9ca3af;}
.ak-dark .picking-dialog__body {scrollbar-color: rgba(255, 255, 255, .35) transparent;}
.ak-dark .picking-dialog__body::-webkit-scrollbar-thumb {background-color: rgba(255, 255, 255, .35);}
.picking-field {display: flex; flex-direction: column; gap: 8px; margin-bottom: 24px;}
.picking-field label, .picking-dialog h3 {color: var(--picking-dialog-muted); font-size: 14px; font-weight: 400;}
.picking-field input, .picking-field textarea, .picking-field select {box-sizing: border-box; width: 100%; min-height: 52px; padding: 12px 16px; color: var(--picking-dialog-text); background: var(--picking-dialog-panel); border: 1px solid var(--picking-dialog-border); border-radius: 8px; font: inherit;}
.picking-field textarea {min-height: 98px; resize: vertical;}
.picking-field input:focus, .picking-field textarea:focus, .picking-field select:focus {border-color: #298afb; outline: 1px solid #298afb;}
.picking-field input.is-invalid {border-color: var(--color-danger, #d92d20);}
.picking-field small, .picking-line__error {color: var(--color-danger, #d92d20);}
.picking-dialog__intro {display: grid; grid-template-columns: minmax(260px, 1fr) minmax(0, 2fr); gap: 32px;}
.picking-dialog__intro .picking-field textarea {min-height: 52px;}
.picking-order-info {display: grid; min-width: 0; grid-template-columns: minmax(150px, .6fr) minmax(240px, 1.4fr); gap: 16px;}
.picking-order-info > :only-child {grid-column: 1 / -1;}
.picking-order-info__item {min-width: 0;}
.picking-order-info__item span {display: block; margin-bottom: 4px; color: var(--picking-dialog-muted); font-size: 14px;}
.picking-order-info__item strong {font-size: 16px;}
.picking-order-info__comments {min-height: 0;}
.picking-dialog__header .picking-order-info__comments p {display: block; max-height: 72px; margin: 0; overflow-y: auto; line-height: 1.5; white-space: pre-wrap;}
.picking-dialog__columns {display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 32px;}
.picking-dialog__columns--without-order-lines {grid-template-columns: repeat(2, minmax(0, 1fr));}
.picking-dialog h3 {margin: 0 0 8px;}
.picking-lines {padding: 0 22px; background: var(--picking-dialog-panel); border: 1px solid var(--picking-dialog-border); border-radius: 8px;}
.picking-line {position: relative; display: flex; align-items: center; justify-content: space-between; min-height: 52px; border-bottom: 1px solid var(--picking-dialog-border);}
.picking-line:last-child {border-bottom: 0;}
.picking-line--material.is-addable {cursor: pointer;}
.picking-line--material.is-addable:hover {background: rgba(41, 138, 251, .06);}
.picking-line__name {display: flex; min-width: 0; gap: 8px; padding-right: 16px; align-items: baseline; font-size: 15px;}
.picking-line__name span {overflow: hidden; text-overflow: ellipsis; white-space: nowrap;}
.picking-line__name small {color: var(--picking-dialog-muted); white-space: nowrap;}
.picking-line__error {position: absolute; top: calc(100% - 4px); left: 0; z-index: 1; padding: 2px 4px; background: var(--picking-dialog-panel);}
.picking-counter {display: flex; align-items: center; gap: 4px; flex-shrink: 0;}
.picking-counter button {display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; color: var(--picking-dialog-text); background: var(--picking-dialog-control); border: 0; border-radius: 50%; cursor: pointer;}
.picking-counter button.is-primary {color: #fff; background: #298afb;}
.picking-dialog button:disabled {cursor: not-allowed;}
.picking-counter button:disabled {opacity: .35;}
.picking-counter strong {min-width: 48px; text-align: center; font-size: 15px;}
.picking-counter input {box-sizing: border-box; width: 56px; height: 44px; padding: 4px; color: var(--picking-dialog-text); background: var(--picking-dialog-control-muted); border: 1px solid transparent; border-radius: 8px; appearance: textfield; font: inherit; font-weight: 600; text-align: center; transition: border-color .15s ease, background-color .15s ease, box-shadow .15s ease;}
.picking-counter input::-webkit-inner-spin-button, .picking-counter input::-webkit-outer-spin-button {margin: 0; appearance: none;}
.picking-counter input:hover {border-color: var(--picking-dialog-border);}
.picking-counter input:focus {background: var(--picking-dialog-panel); border-color: #298afb; outline: 0; box-shadow: 0 0 0 2px rgba(41, 138, 251, .18);}
.picking-shipping {display: flex; flex-direction: column; gap: 8px; margin-top: 32px;}
.picking-shipping--without-order-lines {margin-top: 0;}
.picking-shipping__option {background: var(--picking-dialog-panel); border: 1px solid var(--picking-dialog-border); border-radius: 8px;}
.picking-shipping__option--disabled {background: var(--picking-dialog-control-muted);}
.picking-shipping__option--disabled .picking-shipping__row span {color: var(--picking-dialog-muted);}
.picking-shipping__row {display: flex; align-items: center; justify-content: space-between; gap: 16px; min-height: 52px; padding: 0 24px;}
.picking-shipping__row span {color: var(--picking-dialog-text); font-size: 15px;}
.picking-shipping__row--other {cursor: pointer;}
.picking-shipping__switch {position: relative; box-sizing: border-box; width: 48px; height: 28px; margin: 0; flex-shrink: 0; appearance: none; background: var(--picking-dialog-control); border: 1px solid var(--picking-dialog-border); border-radius: 14px; cursor: pointer; transition: background-color .15s ease, border-color .15s ease;}
.picking-shipping__switch::before {position: absolute; top: 3px; left: 3px; width: 20px; height: 20px; background: var(--picking-dialog-panel); border-radius: 50%; box-shadow: 0 1px 3px rgba(0, 0, 0, .2); content: ''; transition: transform .15s ease;}
.picking-shipping__switch:checked {background: #298afb; border-color: #298afb;}
.picking-shipping__switch:checked::before {transform: translateX(20px);}
.picking-shipping__switch:focus-visible {outline: 2px solid #298afb; outline-offset: 3px;}
.picking-shipping__type {display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px 16px; padding: 12px 24px; border-top: 1px solid var(--picking-dialog-border);}
.picking-shipping__type label {color: var(--picking-dialog-text); font-size: 15px;}
.picking-shipping__type select {box-sizing: border-box; width: min(100%, 300px); min-height: 44px; padding: 8px 48px 8px 12px; color: var(--picking-dialog-text); background-color: var(--picking-dialog-panel); border: 1px solid var(--picking-dialog-border); border-radius: 8px; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='m7 10 5 5 5-5' fill='none' stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='2'/%3E%3C/svg%3E"); background-position: right 14px center; background-repeat: no-repeat; background-size: 20px; font: inherit;}
.picking-shipping__type select:focus {border-color: #298afb; outline: 1px solid #298afb;}
.picking-shipping__weight {display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px 16px; padding: 12px 24px; border-top: 1px solid var(--picking-dialog-border);}
.picking-shipping__weight label {color: var(--picking-dialog-text); font-size: 15px;}
.picking-shipping__weight-input {display: flex; align-items: center; gap: 8px;}
.picking-shipping__weight-input span {color: var(--picking-dialog-text); font-weight: 600;}
.picking-shipping__weight input {box-sizing: border-box; width: 120px; min-height: 44px; padding: 8px 12px; color: var(--picking-dialog-text); background: var(--picking-dialog-panel); border: 1px solid var(--picking-dialog-border); border-radius: 8px; font: inherit; text-align: right;}
.picking-shipping__weight input:focus {border-color: #298afb; outline: 1px solid #298afb;}
.picking-shipping__weight input.is-invalid {border-color: var(--color-danger, #d92d20);}
.picking-shipping__weight small {color: var(--picking-dialog-muted);}
.picking-shipping__weight .picking-shipping__error {flex-basis: 100%; padding: 0; color: var(--color-danger, #d92d20);}
.picking-shipping__error {display: block; padding: 0 24px 12px; color: var(--color-danger, #d92d20);}
.picking-dialog__error {margin: 0; color: var(--color-danger, #d92d20);}
.picking-dialog__footer {display: flex; flex: 0 0 auto; justify-content: flex-end; gap: 16px; padding: 18px 22px; background: var(--picking-dialog-panel); border-top: 1px solid var(--picking-dialog-border);}
.picking-button {min-height: 58px; padding: 0 24px; border: 0; border-radius: 8px; font: inherit; font-size: 16px; cursor: pointer;}
.picking-button--text {color: var(--picking-dialog-muted); background: transparent;}
.picking-button--primary {min-width: 114px; color: #fff; background: #298afb;}
.picking-button:disabled {opacity: .55;}
@media (max-width: 1100px) {
    .picking-dialog__header-meta {grid-template-columns: repeat(2, minmax(0, 1fr));}
    .picking-dialog__header-meta .picking-order-info {grid-column: 1 / -1; margin-top: 16px; padding: 16px 0 0; border-top: 1px solid var(--picking-dialog-border); border-right: 0;}
}
@media (max-width: 760px) {
    .picking-dialog__header-meta, .picking-dialog__header-meta--without-order-info, .picking-dialog__intro, .picking-dialog__columns, .picking-dialog__columns--without-order-lines {grid-template-columns: 1fr; gap: 0;}
    .picking-dialog__meta-group, .picking-dialog__meta-group:first-child {padding: 12px 0; border-right: 0; border-bottom: 1px solid var(--picking-dialog-border);}
    .picking-dialog__header-meta .picking-order-info {margin-top: 0; border-top: 0;}
    .picking-dialog__meta-group:last-child {border-bottom: 0;}
    .picking-order-info {grid-template-columns: 1fr;}
    .picking-order-info > * {grid-column: auto;}
    .picking-dialog__header, .picking-dialog__body {padding-left: 20px; padding-right: 20px;}
}
</style>
